import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeading from '../components/PageHeading';
import { IconLock, IconRefresh } from '../components/Icons';
import { API_ENDPOINTS } from '../config';
import { useDelayedTrueWhile } from '../hooks/useDelayedTrueWhile';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import { getEntityDisplayName } from '../utils/activeEntity';
import { trackEvent } from '../utils/analytics';
import usePersistentTab from '../hooks/usePersistentTab';
import './HedgingRiskPage.css';

const TIER_WAIT_UI_DELAY_MS = 750;

function hedgeLevelLabel(level) {
  if (level === 'high') return 'High hedge risk';
  if (level === 'moderate') return 'Moderate hedge risk';
  if (level === 'low') return 'Low hedge risk';
  return 'Minimal overlap';
}

function hedgeLevelClass(level) {
  return `hedge-risk-level hedge-risk-level--${level || 'minimal'}`;
}

function formatPct(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `${Number(value).toFixed(1)}%`;
}

function formatCorr(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toFixed(2);
}

function formatMinutes(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  if (n < 60) return `${n.toFixed(1)} min`;
  const h = Math.floor(n / 60);
  const m = Math.round(n % 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}

function pairKey(pair, idx = 0) {
  if (pair.strategyA && pair.strategyB) return `${pair.strategyA}::${pair.strategyB}`;
  return `locked-${pair.hedgeScore}-${idx}`;
}

function pairInvolvesStrategy(pair, strategy) {
  if (!strategy) return true;
  return pair.strategyA === strategy || pair.strategyB === strategy;
}

function pairMatchesComparison(pair, strategy, compareWith) {
  if (!compareWith) return pairInvolvesStrategy(pair, strategy);
  return (
    (pair.strategyA === strategy && pair.strategyB === compareWith)
    || (pair.strategyA === compareWith && pair.strategyB === strategy)
  );
}

function otherStrategyInPair(pair, strategy) {
  if (pair.strategyA === strategy) return pair.strategyB;
  if (pair.strategyB === strategy) return pair.strategyA;
  return null;
}

function sortPairsByHedgeScore(pairs) {
  return [...pairs].sort(
    (a, b) => (b.hedgeScore - a.hedgeScore) || ((b.overlapMinutes || 0) - (a.overlapMinutes || 0)),
  );
}

function getUnlockedPairs(pairs) {
  return (pairs || []).filter((p) => p.strategyA && p.strategyB);
}

/** Client-side Basic gate — matches server limitAnalysisForBasic; also covers admin tier preview. */
function resolveTierPairs(data, isPro) {
  const unlocked = sortPairsByHedgeScore(getUnlockedPairs(data?.pairs));
  const totalPairCount = data?.totalPairCount ?? unlocked.length + (data?.lockedPairCount || 0);

  if (isPro) {
    return {
      displayPairs: unlocked,
      hiddenPairCount: 0,
      totalPairCount,
    };
  }

  const displayPairs = unlocked.slice(0, 1);
  const hiddenPairCount = Math.max(0, totalPairCount - displayPairs.length);
  return {
    displayPairs,
    hiddenPairCount,
    totalPairCount,
  };
}

function PairCard({ pair, locked = false, onUpgrade }) {
  const handleUpgrade = () => {
    if (onUpgrade) onUpgrade();
  };

  if (locked) {
    return (
      <article className="hedge-risk-pair-card hedge-risk-pair-card--locked" aria-hidden="true">
        <div className="hedge-risk-pair-lock-overlay">
          <IconLock size={28} />
          <p>Upgrade to Pro to see this pair</p>
          <button type="button" className="hedge-risk-upgrade-inline" onClick={handleUpgrade}>
            Upgrade to Pro
          </button>
        </div>
        <div className="hedge-risk-pair-blur">
          <header className="hedge-risk-pair-header">
            <h3>
              <span className="hedge-risk-strat-mask">Strategy A</span>
              <span className="hedge-risk-vs">vs</span>
              <span className="hedge-risk-strat-mask">Strategy B</span>
            </h3>
            <span className={hedgeLevelClass(pair.hedgeLevel)}>
              Score {pair.hedgeScore}
            </span>
          </header>
          <div className="hedge-risk-metrics-grid">
            <div><span>Overlap</span><strong>—</strong></div>
            <div><span>Same-day opposite</span><strong>—</strong></div>
            <div><span>Daily P&amp;L corr.</span><strong>—</strong></div>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="hedge-risk-pair-card">
      <header className="hedge-risk-pair-header">
        <h3>
          <span className="hedge-risk-strat-name">{pair.strategyA}</span>
          <span className="hedge-risk-vs">vs</span>
          <span className="hedge-risk-strat-name">{pair.strategyB}</span>
        </h3>
        <span className={hedgeLevelClass(pair.hedgeLevel)}>
          {hedgeLevelLabel(pair.hedgeLevel)} · {pair.hedgeScore}
        </span>
      </header>

      <div className="hedge-risk-metrics-grid">
        <div>
          <span>Intraday overlap</span>
          <strong>{formatMinutes(pair.overlapMinutes)}</strong>
          <small>{pair.overlapEvents} event{pair.overlapEvents === 1 ? '' : 's'}</small>
        </div>
        <div>
          <span>Overlap vs time in market</span>
          <strong>{formatPct(pair.overlapPctA)} / {formatPct(pair.overlapPctB)}</strong>
          <small>{pair.strategyA} / {pair.strategyB}</small>
        </div>
        <div>
          <span>Same-day opposite</span>
          <strong>{formatPct(pair.sameDayOppositePct)}</strong>
          <small>{pair.sameDayOppositeDays} of {pair.sharedTradingDays} shared days</small>
        </div>
        <div>
          <span>Daily P&amp;L correlation</span>
          <strong>{formatCorr(pair.dailyPnlCorrelation)}</strong>
          <small>Negative = offsetting days</small>
        </div>
      </div>

      {pair.examples?.length ? (
        <div className="hedge-risk-examples">
          <h4>Recent overlap examples</h4>
          <ul>
            {pair.examples.map((ex) => (
              <li key={`${ex.date}-${ex.overlapMinutes}-${ex.strategyA.entryTime}`}>
                <span className="hedge-risk-example-date">{ex.date}</span>
                <span className="hedge-risk-example-detail">
                  {ex.instrument}: {ex.strategyA.side} ({pair.strategyA}) overlapped{' '}
                  {ex.strategyB.side} ({pair.strategyB}) for {formatMinutes(ex.overlapMinutes)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </article>
  );
}

function PairTable({ pairs, onSelectPair, onUpgrade }) {
  const handleUpgrade = () => {
    if (onUpgrade) onUpgrade();
  };

  return (
    <div className="hedge-risk-table-wrap">
      <table className="hedge-risk-table">
        <thead>
          <tr>
            <th>Strategy A</th>
            <th>Strategy B</th>
            <th>Risk</th>
            <th>Score</th>
            <th>Intraday overlap</th>
            <th>Same-day opposite</th>
            <th>Daily P&amp;L corr.</th>
          </tr>
        </thead>
        <tbody>
          {pairs.map((pair, idx) => {
            const locked = !pair.strategyA || !pair.strategyB;
            const key = pairKey(pair, idx);
            if (locked) {
              return (
                <tr key={key} className="hedge-risk-table-row hedge-risk-table-row--locked">
                  <td colSpan={2}>
                    <span className="hedge-risk-strat-mask">Hidden pair</span>
                    <button type="button" className="hedge-risk-table-unlock" onClick={handleUpgrade}>
                      Upgrade to Pro
                    </button>
                  </td>
                  <td>
                    <span className={hedgeLevelClass(pair.hedgeLevel)}>
                      {hedgeLevelLabel(pair.hedgeLevel)}
                    </span>
                  </td>
                  <td>{pair.hedgeScore}</td>
                  <td colSpan={3}>—</td>
                </tr>
              );
            }
            return (
              <tr
                key={key}
                className="hedge-risk-table-row"
                tabIndex={0}
                role="button"
                onClick={() => onSelectPair(pair)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectPair(pair);
                  }
                }}
              >
                <td>{pair.strategyA}</td>
                <td>{pair.strategyB}</td>
                <td>
                  <span className={hedgeLevelClass(pair.hedgeLevel)}>
                    {hedgeLevelLabel(pair.hedgeLevel)}
                  </span>
                </td>
                <td className="hedge-risk-table-num">{pair.hedgeScore}</td>
                <td>{formatMinutes(pair.overlapMinutes)}</td>
                <td>{formatPct(pair.sameDayOppositePct)}</td>
                <td>{formatCorr(pair.dailyPnlCorrelation)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function HedgingRiskPage({
  user,
  isPro,
  tierReady = true,
  authLoading = false,
  onUpgrade,
  onNavigate,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [filterStrategy, setFilterStrategy] = useState('');
  const [filterCompare, setFilterCompare] = useState('');
  const [viewMode, setViewMode] = usePersistentTab('hedgingRisk.view', 'cards', ['cards', 'table']);
  const showTierWaitUi = useDelayedTrueWhile(!tierReady, TIER_WAIT_UI_DELAY_MS);
  const signedIn = Boolean(user?.email && String(user.email).trim());
  const brandLabel = getEntityDisplayName();

  const handleUpgrade = useCallback(() => {
    if (onUpgrade) onUpgrade();
  }, [onUpgrade]);

  const loadAnalysis = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(API_ENDPOINTS.hedgingRiskAnalysis, {
        headers: getBearerAuthHeaders(),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || !json.success) {
        throw new Error(json.error || 'Failed to load hedging analysis');
      }
      setData(json);
    } catch (err) {
      setError(err.message || 'Failed to load hedging analysis');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    trackEvent('hedging_risk_opened', {
      source: 'hedging_risk_page',
      has_user: signedIn,
      is_pro: isPro,
    });
  }, [signedIn, isPro]);

  useEffect(() => {
    if (!tierReady || !signedIn) {
      setLoading(false);
      return undefined;
    }
    loadAnalysis();
    return undefined;
  }, [tierReady, signedIn, loadAnalysis]);

  const { displayPairs, hiddenPairCount, totalPairCount } = useMemo(
    () => resolveTierPairs(data, isPro),
    [data, isPro],
  );

  const compareOptions = useMemo(() => {
    if (!filterStrategy || !isPro) return [];
    const others = new Set();
    for (const pair of data?.pairs || []) {
      const other = otherStrategyInPair(pair, filterStrategy);
      if (other) others.add(other);
    }
    return [...others].sort((a, b) => a.localeCompare(b));
  }, [data, filterStrategy, isPro]);

  const filteredPairs = useMemo(() => {
    let list = displayPairs;
    if (isPro && filterStrategy) {
      list = list.filter((p) => pairInvolvesStrategy(p, filterStrategy));
    }
    if (isPro && filterStrategy && filterCompare) {
      list = list.filter((p) => pairMatchesComparison(p, filterStrategy, filterCompare));
    }
    return list;
  }, [displayPairs, filterStrategy, filterCompare, isPro]);

  const unlockedFilteredPairs = useMemo(
    () => filteredPairs.filter((p) => p.strategyA && p.strategyB),
    [filteredPairs],
  );

  const showDetailPanel = filterStrategy && filterCompare && unlockedFilteredPairs.length === 1;

  const handleStrategyChange = (value) => {
    setFilterStrategy(value);
    setFilterCompare('');
  };

  const handleSelectPairFromTable = (pair) => {
    if (!pair.strategyA || !pair.strategyB) return;
    setFilterStrategy(pair.strategyA);
    setFilterCompare(pair.strategyB);
    setViewMode('cards');
  };

  const handleClearFilters = () => {
    setFilterStrategy('');
    setFilterCompare('');
  };

  const pageHeading = (
    <PageHeading
      title="Hedging Risk"
      description="Detect when futures strategies take opposite positions at the same time — combining them can flatten P&amp;L instead of stacking it."
    />
  );

  if (!tierReady) {
    if (!showTierWaitUi) return null;
    return (
      <>
        {pageHeading}
        <div className="results-tier-placeholder" role="status" aria-live="polite" aria-busy="true">
          <div className="results-tier-placeholder-inner">
            <span className="results-tier-placeholder-spin" aria-hidden="true">
              <IconRefresh size={24} color="rgba(129, 140, 248, 0.9)" />
            </span>
            <p className="results-tier-placeholder-text">Loading your account access…</p>
          </div>
        </div>
      </>
    );
  }

  if (authLoading && !signedIn) {
    return (
      <div className="feature-gate-stack">
        {pageHeading}
        <div className="results-tier-placeholder" role="status" aria-live="polite" aria-busy="true">
          <div className="results-tier-placeholder-inner">
            <span className="results-tier-placeholder-spin" aria-hidden="true">
              <IconRefresh size={24} color="rgba(129, 140, 248, 0.9)" />
            </span>
            <p className="results-tier-placeholder-text">Loading…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!signedIn) {
    return (
      <div className="feature-gate-stack">
        {pageHeading}
        <div className="upgrade-required-page">
          <div className="upgrade-required-content">
            <div className="pro-lock-icon">
              <IconLock size={48} />
            </div>
            <h2 className="pro-lock-title">Hedging Risk</h2>
            <p className="pro-lock-subtitle">Sign in to continue</p>
            <p className="pro-lock-description">
              Sign in to analyze futures strategies for overlapping opposite-side exposure.
            </p>
            <button
              type="button"
              className="pro-lock-upgrade-button"
              onClick={() => onNavigate && onNavigate('login')}
            >
              Sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hedge-risk-page">
      {pageHeading}

      <div className="hedge-risk-intro">
        <p>
          As trade data comes in for <strong>{brandLabel}</strong>, we measure intraday overlap
          (Long vs Short on the same instrument), same-day opposite direction, and daily P&amp;L
          correlation.
        </p>
        {!isPro ? (
          <div className="hedge-risk-basic-banner" role="note">
            <strong>Basic preview:</strong> one hedging pair is shown below. Upgrade to Pro to
            unlock all {totalPairCount || 'strategy'} pairings
            {hiddenPairCount ? ` (${hiddenPairCount} hidden)` : ''}.
          </div>
        ) : null}
      </div>

      {loading ? (
        <div className="results-tier-placeholder" role="status" aria-live="polite" aria-busy="true">
          <div className="results-tier-placeholder-inner">
            <span className="results-tier-placeholder-spin" aria-hidden="true">
              <IconRefresh size={24} color="rgba(129, 140, 248, 0.9)" />
            </span>
            <p className="results-tier-placeholder-text">Analyzing trades…</p>
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="hedge-risk-error" role="alert">{error}</div>
      ) : null}

      {!loading && !error && data ? (
        <>
          <div className="hedge-risk-summary">
            <div className="hedge-risk-summary-stat">
              <span>Futures strategies</span>
              <strong>{data.strategies?.length || 0}</strong>
            </div>
            <div className="hedge-risk-summary-stat">
              <span>Trades analyzed</span>
              <strong>{data.tradeCount || 0}</strong>
            </div>
            <div className="hedge-risk-summary-stat">
              <span>Strategy pairs</span>
              <strong>{totalPairCount || 0}</strong>
            </div>
          </div>

          {!data.strategies?.length || !data.tradeCount ? (
            <div className="hedge-risk-empty">
              <h2>No trades yet</h2>
              <p>
                Hedging analysis needs completed trades. Once trade data arrives for{' '}
                {brandLabel}, pairings will appear here automatically.
              </p>
              {isPro ? null : (
                <button type="button" className="hedge-risk-upgrade-cta" onClick={handleUpgrade}>
                  Upgrade to Pro — unlock every pair when data arrives
                </button>
              )}
            </div>
          ) : null}

          {data.strategies?.length === 1 ? (
            <div className="hedge-risk-empty">
              <h2>Need at least two strategies</h2>
              <p>
                Only one futures strategy has trade data so far ({data.strategies[0]}).
                Hedging checks start once a second strategy has trades.
              </p>
            </div>
          ) : null}

          {displayPairs.length ? (
            <section className="hedge-risk-pairs" aria-label="Strategy hedge pairings">
              <div className="hedge-risk-pairs-header">
                <div>
                  <h2>Strategy pairings</h2>
                  <span>
                    {isPro
                      ? 'Sorted by hedge score (highest first)'
                      : 'Top hedge-risk pair on Basic — upgrade for the full list'}
                  </span>
                </div>
                {isPro ? (
                  <div className="hedge-risk-view-toggle">
                    <button
                      type="button"
                      className={`hedge-risk-view-btn ${viewMode === 'cards' ? 'active' : ''}`}
                      onClick={() => setViewMode('cards')}
                    >
                      Cards
                    </button>
                    <button
                      type="button"
                      className={`hedge-risk-view-btn ${viewMode === 'table' ? 'active' : ''}`}
                      onClick={() => setViewMode('table')}
                    >
                      Table
                    </button>
                  </div>
                ) : null}
              </div>

              {isPro ? (
                <div className="hedge-risk-controls">
                  <label className="hedge-risk-control">
                    <span className="hedge-risk-control-label">Strategy</span>
                    <select
                      value={filterStrategy}
                      onChange={(e) => handleStrategyChange(e.target.value)}
                    >
                      <option value="">All strategies</option>
                      {(data.strategies || []).map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </label>
                  <label className="hedge-risk-control">
                    <span className="hedge-risk-control-label">Compare with</span>
                    <select
                      value={filterCompare}
                      onChange={(e) => setFilterCompare(e.target.value)}
                      disabled={!filterStrategy}
                    >
                      <option value="">All pairings for strategy</option>
                      {compareOptions.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </label>
                  {(filterStrategy || filterCompare) ? (
                    <button type="button" className="hedge-risk-clear-filters" onClick={handleClearFilters}>
                      Clear filters
                    </button>
                  ) : null}
                </div>
              ) : null}

              {filteredPairs.length === 0 ? (
                <div className="hedge-risk-empty">
                  <h2>No pairings match your filters</h2>
                  <p>Try a different strategy or clear filters to see all pairings.</p>
                </div>
              ) : null}

              {viewMode === 'table' && isPro && filteredPairs.length > 0 ? (
                <>
                  <PairTable
                    pairs={filteredPairs}
                    onSelectPair={handleSelectPairFromTable}
                    onUpgrade={onUpgrade}
                  />
                  <p className="hedge-risk-table-hint">Click a row to open that pairing in card view.</p>
                </>
              ) : null}

              {(viewMode === 'cards' || !isPro) && filteredPairs.length > 0 ? (
                <div className={`hedge-risk-pairs-grid${showDetailPanel ? ' hedge-risk-pairs-grid--single' : ''}`}>
                  {filteredPairs.map((pair, idx) => (
                    <PairCard
                      key={pairKey(pair, idx)}
                      pair={pair}
                      locked={false}
                      onUpgrade={onUpgrade}
                    />
                  ))}
                </div>
              ) : null}

              {!isPro && hiddenPairCount > 0 ? (
                <div className="hedge-risk-basic-upsell">
                  <IconLock size={24} />
                  <div>
                    <strong>{hiddenPairCount} more pairing{hiddenPairCount === 1 ? '' : 's'} hidden on Basic</strong>
                    <p>Upgrade to Pro to see every strategy pair, overlap details, and correlation scores.</p>
                  </div>
                  <button type="button" className="hedge-risk-upgrade-cta" onClick={handleUpgrade}>
                    Upgrade to Pro
                  </button>
                </div>
              ) : null}
            </section>
          ) : null}

          {data.strategies?.length >= 2 && !displayPairs.length && data.tradeCount > 0 ? (
            <div className="hedge-risk-empty hedge-risk-empty--positive">
              <h2>No meaningful hedging detected</h2>
              <p>
                These strategies rarely take opposite positions at the same time. Combining them in
                a basket is unlikely to cancel P&amp;L through hedging.
              </p>
            </div>
          ) : null}

        </>
      ) : null}
    </div>
  );
}
